import { ClosedEndedStatement } from 'types/interface';

export type RateMode = 'statementDate' | 'today';

const NUMERIC_FIELDS = [
   'totalCommitment',
   'capitalCalled',
   'remainingCommitment',
   'distributed',
   'nav',
] as const;

const numberFormatter = new Intl.NumberFormat('en-US', {
   maximumFractionDigits: 0,
});

/** Formats a value as a plain grouped number without a currency symbol. */
export function formatNumber(value: number): string {
   return numberFormatter.format(value);
}

/** Formats a value as an ILS amount (₪). */
export function formatIls(value: number): string {
   return `₪${numberFormatter.format(value)}`;
}

/** True when the investments span more than one currency. */
export function hasMixedCurrencies(
   investments: ClosedEndedStatement[],
): boolean {
   const currencies = new Set(
      investments.map(investment => investment.account.currency),
   );
   return currencies.size > 1;
}

/** Picks the exchange rate for the selected conversion mode. */
export function getRate(
   statement: ClosedEndedStatement,
   mode: RateMode,
): number | null | undefined {
   return mode === 'today' ? statement.todayRate : statement.statementDateRate;
}

/** Converts a value to its ILS equivalent, or null when no rate is available. */
export function toIls(
   value: number,
   rate: number | null | undefined,
): number | null {
   return rate == null ? null : value * rate;
}

/**
 * Returns a copy of the statement with all numeric fields converted to their
 * ILS equivalent using the selected rate. A missing rate converts to 0 so the
 * graphs still render without breaking.
 */
export function toIlsStatement(
   statement: ClosedEndedStatement,
   mode: RateMode,
): ClosedEndedStatement {
   const rate = getRate(statement, mode);
   const converted = { ...statement };
   for (const field of NUMERIC_FIELDS) {
      converted[field] = toIls(statement[field], rate) ?? 0;
   }
   return converted;
}
